import PropTypes from "prop-types";
import { Badge, Box, Button, Checkbox, Flex, Image, SimpleGrid, Stack, Text, Textarea } from "@chakra-ui/react";
import { formatDateTime, isUrl } from "./verificationStatus";

const ValuePreview = ({ value, type, label }) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  if (!values.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        {type === "text" ? "—" : "Not uploaded"}
      </Text>
    );
  }
  if (type === "text") {
    return (
      <Text fontSize="sm" fontWeight="semibold" color="gray.800" wordBreak="break-word">
        {String(values.join(", "))}
      </Text>
    );
  }
  return (
    <SimpleGrid columns={values.length > 1 ? 2 : 1} spacing={2}>
      {values.map((url, index) =>
        isUrl(url) ? (
          <Box key={`${url}-${index}`}>
            <Box borderRadius="md" overflow="hidden" bg="gray.50">
              <Image src={url} alt={label} w="100%" h="130px" objectFit="cover" />
            </Box>
            <Button as="a" href={url} target="_blank" rel="noreferrer" mt={2} size="xs" variant="outline">
              Open Full Image
            </Button>
          </Box>
        ) : (
          <Text key={index} fontSize="sm">
            {String(url)}
          </Text>
        ),
      )}
    </SimpleGrid>
  );
};

ValuePreview.propTypes = {
  value: PropTypes.any,
  type: PropTypes.string,
  label: PropTypes.string,
};

/**
 * One reviewable field/document.
 * - selectable: shows checkbox + reason box (Request Clarification mode)
 * - requested:  the field is in the open clarification round (orange badge + admin reason)
 * - resubmitted: vendor resubmitted it in the latest round (green badge, old -> new value)
 */
const ReviewFieldTile = ({
  field,
  value,
  requested = null,
  selectable = false,
  selected = false,
  reason = "",
  onToggle,
  onReasonChange,
}) => {
  const isResubmitted = !!requested?.isResubmitted;
  const isOpenRequest = !!requested && !isResubmitted;

  let borderColor = "gray.200";
  if (isResubmitted) borderColor = "green.300";
  else if (isOpenRequest) borderColor = "orange.300";
  if (selected) borderColor = "blue.400";

  return (
    <Box border="1px solid" borderColor={borderColor} borderRadius="lg" p={3} bg={selected ? "blue.50" : "white"}>
      <Flex justify="space-between" align="flex-start" gap={2} mb={2}>
        {selectable ? (
          <Checkbox isChecked={selected} onChange={() => onToggle?.(field)}>
            <Text fontSize="xs" textTransform="uppercase" color="gray.600" fontWeight="semibold">
              {field.fieldLabel}
            </Text>
          </Checkbox>
        ) : (
          <Text fontSize="xs" textTransform="uppercase" color="gray.500">
            {field.fieldLabel}
          </Text>
        )}
        <Stack direction="row" spacing={1}>
          {isOpenRequest && <Badge colorScheme="orange">Clarification Required</Badge>}
          {isResubmitted && (
            <Badge colorScheme="green" title={formatDateTime(requested.resubmittedAt)}>
              Resubmitted · {formatDateTime(requested.resubmittedAt)}
            </Badge>
          )}
        </Stack>
      </Flex>

      {isResubmitted ? (
        <SimpleGrid columns={2} spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Old
            </Text>
            <ValuePreview value={requested.previousValue} type={field.type} label={field.fieldLabel} />
          </Box>
          <Box>
            <Text fontSize="xs" color="green.600" mb={1}>
              New
            </Text>
            <ValuePreview value={requested.resubmittedValue ?? value} type={field.type} label={field.fieldLabel} />
          </Box>
        </SimpleGrid>
      ) : (
        <ValuePreview value={value} type={field.type} label={field.fieldLabel} />
      )}

      {requested?.reason && (
        <Text mt={2} fontSize="xs" color={isResubmitted ? "gray.600" : "orange.700"}>
          Admin reason: {requested.reason}
        </Text>
      )}

      {selectable && selected && (
        <Textarea
          mt={2}
          size="sm"
          placeholder="Reason for the vendor (required), e.g. Uploaded image is unclear"
          value={reason}
          onChange={(e) => onReasonChange?.(field, e.target.value)}
          isInvalid={!reason.trim()}
        />
      )}
    </Box>
  );
};

ReviewFieldTile.propTypes = {
  field: PropTypes.shape({
    fieldKey: PropTypes.string.isRequired,
    fieldLabel: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  value: PropTypes.any,
  requested: PropTypes.object,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  reason: PropTypes.string,
  onToggle: PropTypes.func,
  onReasonChange: PropTypes.func,
};

export default ReviewFieldTile;
